import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import matchChatAPI from '../services/matchChatApi';
import { syncPlayerNotificationsAfterMatchChat } from '../utils/playerNotificationQueries';
import { publicAssetUrl } from '../utils/publicAssetUrl';

function messageSenderLabel(msg) {
  const playerName = msg?.sender_player_name && String(msg.sender_player_name).trim();
  if (playerName) return playerName;
  if (msg?.is_system_message) return msg.sender_team_name || 'Match schedule';
  return msg?.sender_team_name || 'Player';
}

function senderAvatarUrl(msg) {
  const raw = msg?.sender_profile_picture_url;
  if (!raw || !String(raw).trim()) return '';
  const s = String(raw).trim();
  if (s.startsWith('data:') || s.startsWith('http://') || s.startsWith('https://')) return s;
  return publicAssetUrl(s);
}

function seenReceiptText(receipt, t, viewerPlayerId) {
  if (!receipt) return '';
  if (receipt.everyone) return t('phase2.matchChat.seenByEveryone');
  const readers = Array.isArray(receipt.readers) && receipt.readers.length
    ? receipt.readers
    : (Array.isArray(receipt.names) ? receipt.names.map((name) => ({ name })) : []);
  if (!readers.length) return '';
  const viewerId = viewerPlayerId ? String(viewerPlayerId) : '';
  const otherNames = [];
  let includesYou = false;
  readers.forEach((reader) => {
    const id = reader?.player_id ? String(reader.player_id) : '';
    if (viewerId && id && id === viewerId) {
      includesYou = true;
      return;
    }
    if (reader?.name) otherNames.push(reader.name);
  });
  const ordered = includesYou
    ? [t('phase2.matchChat.seenByYou'), ...otherNames]
    : otherNames;
  if (!ordered.length) return '';
  if (ordered.length === 1) return t('phase2.matchChat.seenBy', { names: ordered[0] });
  const last = ordered[ordered.length - 1];
  const rest = ordered.slice(0, -1).join(', ');
  return t('phase2.matchChat.seenBy', { names: `${rest} & ${last}` });
}

function senderInitials(msg) {
  const name = messageSenderLabel(msg);
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

const MatchChatModal = ({
  isOpen,
  onClose,
  matchId,
  opponentName,
  opponentTeamId,
  teamId,
  viewerPlayerId = null,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const [opponentIsTyping, setOpponentIsTyping] = useState(false);
  const [opponentPhoneNumber, setOpponentPhoneNumber] = useState(null);
  const [phoneRevelationStatus, setPhoneRevelationStatus] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputContainerRef = useRef(null);
  const messageInputRef = useRef(null);
  const modalContainerRef = useRef(null);
  const pollingInterval = useRef(null);
  const typingTimeout = useRef(null);
  const typingAbort = useRef(null);
  const typingSeq = useRef(0);
  const loadMessagesInFlight = useRef(false);

  const isNearBottom = () => {
    if (!messagesContainerRef.current) return true;
    const container = messagesContainerRef.current;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    return scrollHeight - scrollTop - clientHeight < 150;
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleInputFocus = () => {
    setTimeout(() => {
      if (inputContainerRef.current) {
        inputContainerRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
          inline: 'nearest',
        });
      }
    }, 150);
  };

  const checkPhoneNumberRevelation = async () => {
    if (!opponentTeamId) return;

    try {
      const response = await matchChatAPI.getPhoneNumber(matchId, opponentTeamId, teamId);

      if (response.success) {
        setPhoneRevelationStatus(response.data);
        if (response.data.canSeePhone && response.data.phoneNumber) {
          setOpponentPhoneNumber(response.data.phoneNumber);
        }
      }
    } catch (_err) {
      // Silent — chat still works without phone
    }
  };

  const loadMessages = async (showLoading = true) => {
    if (loadMessagesInFlight.current) {
      console.debug('Chat load skipped (previous request still in flight)');
      return;
    }
    loadMessagesInFlight.current = true;
    try {
      if (showLoading) setLoading(true);
      setError(null);

      const wasNearBottom = isNearBottom();
      const previousMessageCount = messages.length;

      const response = await matchChatAPI.getMessages(matchId, teamId);

      if (response.success) {
        const newMessages = response.messages || [];

        const hasNewMessages = newMessages.length > previousMessageCount;

        setMessages(newMessages);
        setOpponentIsTyping(response.opponentIsTyping || false);
        setLastFetched(new Date());

        await checkPhoneNumberRevelation();

        if (showLoading || (wasNearBottom && hasNewMessages)) {
          setTimeout(scrollToBottom, 100);
        }
      }
    } catch (err) {
      const aborted =
        err?.code === 'ERR_CANCELED' ||
        err?.name === 'CanceledError' ||
        (typeof err?.message === 'string' && err.message.includes('aborted'));
      if (aborted) {
        console.debug('Chat messages request aborted (poll overlap or navigation); ignoring');
        return;
      }
      console.error('❌ Error loading messages:', err);
      setError('Failed to load messages');
    } finally {
      loadMessagesInFlight.current = false;
      if (showLoading) setLoading(false);
    }
  };

  const postTyping = (active) => {
    const seq = ++typingSeq.current;
    if (typingAbort.current) {
      typingAbort.current.abort();
    }
    const controller = new AbortController();
    typingAbort.current = controller;
    matchChatAPI.updateTypingStatus(matchId, teamId, {
      active,
      seq,
      signal: controller.signal,
    });
  };

  const handleTyping = () => {
    if (typingTimeout.current) {
      clearTimeout(typingTimeout.current);
    }

    postTyping(true);

    typingTimeout.current = setTimeout(() => {
      postTyping(false);
    }, 2500);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!newMessage.trim()) return;

    if (newMessage.length > 2000) {
      setError('Message is too long (max 2000 characters)');
      return;
    }

    try {
      setSending(true);
      setError(null);

      if (typingTimeout.current) {
        clearTimeout(typingTimeout.current);
        typingTimeout.current = null;
      }
      postTyping(false);

      const response = await matchChatAPI.sendMessage(matchId, newMessage.trim(), teamId);

      if (response.pushDebug) {
        console.log('[match-chat push debug]', response.pushDebug);
        try {
          console.log('[match-chat push debug JSON]', JSON.stringify(response.pushDebug, null, 2));
        } catch (_) {
          /* ignore */
        }
      }

      if (response.success) {
        setMessages((prev) => [...prev, response.message]);
        setNewMessage('');
        setTimeout(scrollToBottom, 100);
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (isOpen && matchId) {
      loadMessages();

      pollingInterval.current = setInterval(() => {
        loadMessages(false);
      }, 5000);

      return () => {
        if (pollingInterval.current) {
          clearInterval(pollingInterval.current);
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, matchId, teamId]);

  useEffect(() => {
    if (!isOpen || !matchId || !teamId) return undefined;
    const syncReadState = async () => {
      try {
        await matchChatAPI.markAsRead(matchId, teamId);
      } catch {
        /* match chat read is best-effort */
      }
      try {
        await matchChatAPI.postViewing(matchId, teamId);
      } catch {
        /* viewing heartbeat is best-effort */
      }
      syncPlayerNotificationsAfterMatchChat(queryClient);
    };
    syncReadState();
    const id = setInterval(() => {
      matchChatAPI.postViewing(matchId, teamId).catch(() => {});
    }, 15000);
    return () => {
      clearInterval(id);
      syncPlayerNotificationsAfterMatchChat(queryClient);
    };
  }, [isOpen, matchId, teamId, queryClient]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const messagesContainer = messagesContainerRef.current;
    const modalContainer = modalContainerRef.current;
    if (!messagesContainer || !modalContainer || !isOpen) return;

    let touchStartY = 0;

    const handleWheel = (e) => {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainer;
      const isAtTop = scrollTop <= 1;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;
      const hasScroll = scrollHeight > clientHeight;

      if (!hasScroll || (isAtTop && e.deltaY < 0) || (isAtBottom && e.deltaY > 0)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleTouchStart = (e) => {
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchMove = (e) => {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainer;
      const isAtTop = scrollTop <= 1;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;
      const hasScroll = scrollHeight > clientHeight;
      const touchY = e.touches[0].clientY;
      const scrollingDown = touchY < touchStartY;
      const scrollingUp = touchY > touchStartY;

      if (!hasScroll || (isAtTop && scrollingUp) || (isAtBottom && scrollingDown)) {
        const target = e.target;
        if (
          !modalContainer.contains(target) ||
          target === messagesContainer ||
          messagesContainer.contains(target)
        ) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
      }
    };

    messagesContainer.addEventListener('wheel', handleWheel, { passive: false });
    modalContainer.addEventListener('wheel', handleWheel, { passive: false });
    modalContainer.addEventListener('touchstart', handleTouchStart, { passive: true });
    modalContainer.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      messagesContainer.removeEventListener('wheel', handleWheel);
      modalContainer.removeEventListener('wheel', handleWheel);
      modalContainer.removeEventListener('touchstart', handleTouchStart);
      modalContainer.removeEventListener('touchmove', handleTouchMove);
    };
  }, [isOpen]);

  const handleClose = () => {
    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
    }
    document.body.style.overflow = '';
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={handleClose}>
      <ModalContainer ref={modalContainerRef} onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <HeaderContent>
            <Title>Match Chat</Title>
            <Subtitle>vs {opponentName}</Subtitle>

            {opponentPhoneNumber && (
              <PhoneNumberDisplay>
                <PhoneIcon>📞</PhoneIcon>
                <PhoneLabel>Contact Number:</PhoneLabel>
                <PhoneNumber>{opponentPhoneNumber}</PhoneNumber>
                <PhoneReason>{phoneRevelationStatus?.reason || 'Phone number revealed'}</PhoneReason>
              </PhoneNumberDisplay>
            )}

            {lastFetched && (
              <LastUpdated>
                Last updated:{' '}
                {lastFetched.toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  second: '2-digit',
                })}
              </LastUpdated>
            )}
          </HeaderContent>
          <CloseButton onClick={handleClose}>×</CloseButton>
        </ModalHeader>

        <MessagesContainer ref={messagesContainerRef}>
          {loading ? (
            <LoadingMessage>Loading messages...</LoadingMessage>
          ) : messages.length === 0 ? (
            <EmptyState>
              <EmptyIcon>💬</EmptyIcon>
              <EmptyText>No messages yet</EmptyText>
              <EmptySubtext>Start the conversation to coordinate match details!</EmptySubtext>
            </EmptyState>
          ) : (
            <>
              {messages.map((msg) =>
                msg.is_system_message ? (
                  <SystemAnnouncementRow key={msg.id}>
                    <SystemAnnouncementCard>
                      <SystemAnnouncementLabel>
                        {messageSenderLabel(msg)}
                      </SystemAnnouncementLabel>
                      <SystemAnnouncementText>{msg.message}</SystemAnnouncementText>
                      <SystemAnnouncementTime>
                        {new Date(msg.created_at).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </SystemAnnouncementTime>
                      {seenReceiptText(msg.seen_receipt, t, viewerPlayerId) ? (
                        <SeenReceipt>{seenReceiptText(msg.seen_receipt, t, viewerPlayerId)}</SeenReceipt>
                      ) : null}
                    </SystemAnnouncementCard>
                  </SystemAnnouncementRow>
                ) : (
                  <MessageWrapper key={msg.id} isOwn={msg.is_own_message}>
                    <ChatAvatar isOwn={msg.is_own_message} aria-hidden="true">
                      {senderAvatarUrl(msg) ? (
                        <ChatAvatarImage src={senderAvatarUrl(msg)} alt="" />
                      ) : (
                        <ChatAvatarInitials>{senderInitials(msg)}</ChatAvatarInitials>
                      )}
                    </ChatAvatar>
                    <Message isOwn={msg.is_own_message}>
                      <SenderName>{messageSenderLabel(msg)}</SenderName>
                      <MessageText>{msg.message}</MessageText>
                      <MessageTime>
                        {new Date(msg.created_at).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </MessageTime>
                      {seenReceiptText(msg.seen_receipt, t, viewerPlayerId) ? (
                        <SeenReceipt $own={msg.is_own_message}>{seenReceiptText(msg.seen_receipt, t, viewerPlayerId)}</SeenReceipt>
                      ) : null}
                    </Message>
                  </MessageWrapper>
                )
              )}
              {opponentIsTyping && (
                <TypingIndicator>
                  <TypingDots>
                    <TypingDot delay="0s" />
                    <TypingDot delay="0.2s" />
                    <TypingDot delay="0.4s" />
                  </TypingDots>
                  <TypingText>{opponentName} is typing...</TypingText>
                </TypingIndicator>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </MessagesContainer>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <InputContainer ref={inputContainerRef} onSubmit={handleSendMessage}>
          <MessageInput
            ref={messageInputRef}
            value={newMessage}
            onChange={(e) => {
              setNewMessage(e.target.value);
              handleTyping();
            }}
            onFocus={handleInputFocus}
            placeholder="Type your message..."
            maxLength={2000}
            disabled={sending}
            rows={3}
          />
          <InputFooter>
            <CharCount $isNearLimit={newMessage.length > 1800}>{newMessage.length}/2000</CharCount>
            <SendButton type="submit" disabled={!newMessage.trim() || sending}>
              {sending ? 'Sending...' : 'Send 📤'}
            </SendButton>
          </InputFooter>
        </InputContainer>
      </ModalContainer>
    </Overlay>
  );
};

const PhoneNumberDisplay = styled.div`
  background: rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 8px 12px;
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const PhoneIcon = styled.span`
  font-size: 16px;
`;

const PhoneLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  opacity: 0.9;
`;

const PhoneNumber = styled.span`
  font-size: 14px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.2);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
`;

const PhoneReason = styled.span`
  font-size: 10px;
  opacity: 0.8;
  font-style: italic;
  margin-left: auto;
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 0;
    align-items: flex-end;
  }
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  position: relative;

  @media (max-width: 768px) {
    max-height: 90vh;
    border-radius: 16px 16px 0 0;
    max-width: 100%;
  }

  @media (max-height: 700px) {
    max-height: 95vh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 2px solid #e0e0e0;
  background: linear-gradient(135deg, #043663 0%, #d52b1e 100%);
  color: white;

  @media (max-width: 768px) {
    padding: 16px 20px;
  }
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const Title = styled.h2`
  margin: 0 0 4px 0;
  font-size: 24px;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const Subtitle = styled.div`
  font-size: 16px;
  opacity: 0.95;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const LastUpdated = styled.div`
  font-size: 11px;
  opacity: 0.75;
  margin-top: 4px;
  font-style: italic;

  @media (max-width: 768px) {
    font-size: 10px;
  }
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  font-size: 32px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  line-height: 1;
  padding: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  background: #e5ddd5;
  min-height: 200px;
  max-height: calc(80vh - 300px);
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    padding: 16px;
    min-height: 150px;
    max-height: calc(90vh - 280px);
  }

  @media (max-height: 700px) {
    min-height: 100px;
    max-height: calc(95vh - 280px);
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: #666;
  padding: 40px;
  font-size: 16px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  min-height: 200px;

  @media (max-height: 700px) {
    padding: 20px;
    min-height: 100px;
  }
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.5;
`;

const EmptyText = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
`;

const EmptySubtext = styled.div`
  font-size: 14px;
  color: #666;
`;

const MessageWrapper = styled.div`
  display: flex;
  flex-direction: ${(props) => (props.isOwn ? 'row-reverse' : 'row')};
  align-items: flex-end;
  align-self: ${(props) => (props.isOwn ? 'flex-end' : 'flex-start')};
  gap: 8px;
  margin-bottom: 8px;
  max-width: 100%;
`;

const ChatAvatar = styled.div`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  background: ${(props) => (props.isOwn ? '#dfe5e7' : '#cfd8dc')};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
  }
`;

const ChatAvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ChatAvatarInitials = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #54656f;
  letter-spacing: 0.02em;
`;

const SystemAnnouncementRow = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
  width: 100%;
`;

const SystemAnnouncementCard = styled.div`
  max-width: 92%;
  width: 100%;
  padding: 14px 16px;
  border-radius: 12px;
  background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%);
  border: 1px solid #c7d2fe;
  box-shadow: 0 2px 10px rgba(4, 54, 99, 0.08);

  @media (max-width: 768px) {
    max-width: 100%;
    padding: 12px 14px;
  }
`;

const SystemAnnouncementLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #043663;
  margin-bottom: 8px;
`;

const SystemAnnouncementText = styled.div`
  font-size: 14px;
  line-height: 1.55;
  word-wrap: break-word;
  white-space: pre-wrap;
  color: #1f2937;
`;

const SystemAnnouncementTime = styled.div`
  font-size: 11px;
  margin-top: 10px;
  color: #64748b;
`;

const SeenReceipt = styled.div`
  margin-top: 4px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.3;
  color: ${(p) => (p.$own ? '#0f766e' : '#64748b')};
`;

const Message = styled.div`
  max-width: min(72%, 420px);
  background: ${(props) => (props.isOwn ? '#d9fdd3' : '#ffffff')};
  color: #111b21;
  padding: 6px 8px 4px 9px;
  border-radius: ${(props) => (props.isOwn ? '8px 8px 0 8px' : '8px 8px 8px 0')};
  box-shadow: 0 1px 1px rgba(11, 20, 26, 0.13);

  @media (max-width: 768px) {
    max-width: 82%;
    padding: 5px 7px 3px 8px;
  }
`;

const SenderName = styled.div`
  font-size: 12.5px;
  font-weight: 600;
  margin-bottom: 2px;
  color: #1b8755;
  line-height: 1.3;
`;

const MessageText = styled.div`
  font-size: 14.2px;
  line-height: 1.45;
  word-wrap: break-word;
  white-space: pre-wrap;
  padding-right: 4px;
`;

const MessageTime = styled.div`
  font-size: 11px;
  margin-top: 2px;
  text-align: right;
  color: rgba(17, 27, 33, 0.45);
  line-height: 1.2;
`;

const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  margin-bottom: 16px;
`;

const TypingDots = styled.div`
  display: flex;
  gap: 4px;
  padding: 12px 14px;
  background: #ffffff;
  border-radius: 8px 8px 8px 0;
  box-shadow: 0 1px 1px rgba(11, 20, 26, 0.13);
`;

const TypingDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #667eea;
  animation: typing 1.4s infinite;
  animation-delay: ${(props) => props.delay};

  @keyframes typing {
    0%,
    60%,
    100% {
      transform: translateY(0);
      opacity: 0.7;
    }
    30% {
      transform: translateY(-10px);
      opacity: 1;
    }
  }
`;

const TypingText = styled.div`
  font-size: 13px;
  color: #667eea;
  font-style: italic;
  font-weight: 500;
`;

const ErrorMessage = styled.div`
  background: #fee;
  color: #c33;
  padding: 12px 24px;
  text-align: center;
  font-size: 14px;
  border-top: 1px solid #fdd;
`;

const InputContainer = styled.form`
  padding: 20px 24px;
  background: white;
  border-top: 2px solid #e0e0e0;
  flex-shrink: 0;
  position: relative;
  z-index: 10;

  @media (max-width: 768px) {
    padding: 16px;
  }

  @media (max-height: 700px) {
    padding: 12px 16px;
  }
`;

const MessageInput = styled.textarea`
  width: 100%;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 15px;
  font-family: inherit;
  resize: vertical;
  min-height: 60px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #667eea;
  }

  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    font-size: 16px;
    min-height: 80px;
    padding: 12px 14px;
  }
`;

const InputFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
`;

const CharCount = styled.div`
  font-size: 12px;
  color: ${(props) => (props.$isNearLimit ? '#c33' : '#999')};
`;

const SendButton = styled.button`
  background: linear-gradient(135deg, #043663 0%, #d52b1e 100%);
  color: white;
  border: none;
  padding: 12px 32px;
  border-radius: 24px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(4, 54, 99, 0.3);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(4, 54, 99, 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    padding: 12px 24px;
    font-size: 14px;
    width: auto;
    min-width: 80px;
  }
`;

export default MatchChatModal;
